import { AccountInfo, EventType } from '@azure/msal-browser';
import { combineReducers, createAction, createReducer, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { normalize, schema } from 'normalizr';

import {
    AddUserToAppDto,
    AvailableResourcesDto,
    ErrorResponse,
    ResourceDto,
    ResourceType,
    UserDto,
    UserPermissionsDto
} from '../../../common/PricePrgClient';
import { createAsyncAction } from '../../../utils';

const namespaces = {
    users: 'users',
    currentUserPermissions: 'currentUserPermissions',
    availablePermissions: 'availablePermissions'
};

const userEntity = new schema.Entity<UserDto>('users', undefined, {
    idAttribute: 'userId'
});

const pageResourceEntity = new schema.Entity<ResourceDto>('pageResources', undefined, {
    idAttribute: 'resourceType'
});

const recordResourceEntity = new schema.Entity<ResourceDto>('recordResources', undefined, {
    idAttribute: 'resourceType'
});

const availableResourceEntity = new schema.Entity<AvailableResourcesDto>('availableResources', {
    pageResources: [pageResourceEntity],
    recordResources: [recordResourceEntity]
});

export const inviteInternalUser = createAsyncAction<AddUserToAppDto, void, ErrorResponse>(
    namespaces.users,
    'inviteInternalUser'
);
export const inviteExternalUser = createAsyncAction<AddUserToAppDto, void, ErrorResponse>(
    namespaces.users,
    'inviteExternalUser'
);
export const removeUserFromApp = createAsyncAction<number, void>(namespaces.users, 'removeUserFromApp');
export const fetchUsers = createAsyncAction<void, UserDto[]>(namespaces.users, 'fetchUsers');

const usersSlice = createSlice({
    name: namespaces.users,
    initialState: {} as { [userId: number]: UserDto },
    reducers: {
        userAdded: (state, action: PayloadAction<UserDto>) => {
            const { payload } = action;
            state[payload.userId] = payload;
        },
        userRemoved: (state, action: PayloadAction<number>) => {
            delete state[action.payload];
        }
    },
    extraReducers: builder =>
        builder.addCase(fetchUsers.success, (state, action) => {
            const normalized = normalize(action.payload, [userEntity]);
            return normalized.entities.users;
        })
});

export const { userAdded, userRemoved } = usersSlice.actions;

export const fetchCurrentUserPermissions = createAsyncAction<ResourceType[] | null, UserPermissionsDto>(
    namespaces.currentUserPermissions,
    'fetchCurrentUserPermissions'
);

const currentUserPermissionsSlice = createSlice({
    name: namespaces.currentUserPermissions,
    initialState: {
        accessiblePages: [],
        isAdmin: false,
        isInitialized: false,
        recordPermissions: [],
        userGroups: []
    } as UserPermissionsDto & { isInitialized: boolean },
    reducers: {},
    extraReducers: builder =>
        builder.addCase(fetchCurrentUserPermissions.success, (state, action) => ({
            ...action.payload,
            isInitialized: true
        }))
});

export type AvailableResources = {
    pageResources: { [recordTypeId: string]: ResourceDto };
    recordResources: { [recordTypeId: string]: ResourceDto };
};

export const fetchAvailablePermissions = createAsyncAction<void, AvailableResourcesDto>(
    namespaces.availablePermissions,
    'fetchAvailablePermissions'
);

const availableResourcesSlice = createSlice({
    name: namespaces.availablePermissions,
    initialState: { pageResources: {}, recordResources: {} } as AvailableResources,
    reducers: {},
    extraReducers: builder =>
        builder.addCase(fetchAvailablePermissions.success, (state, action) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const entities: any = normalize(action.payload, availableResourceEntity).entities;
            return {
                pageResources: entities.pageResources,
                recordResources: entities.recordResources
            };
        })
});

const inviteInternalUserDialogSlice = createSlice({
    name: 'inviteInternalUserDialog',
    initialState: {
        isSaving: false,
        isOpen: false
    },
    reducers: {
        openInviteInternalUserDialog: (state, action: PayloadAction<boolean>) => {
            state.isOpen = action.payload;
        }
    },
    extraReducers: builder =>
        builder
            .addCase(inviteInternalUser.request, state => {
                state.isSaving = true;
            })
            .addCase(inviteInternalUser.success, state => {
                state.isSaving = false;
                state.isOpen = false;
            })
            .addCase(inviteInternalUser.failure, state => {
                state.isSaving = false;
            })
});
export const { openInviteInternalUserDialog } = inviteInternalUserDialogSlice.actions;

const inviteExternalUserDialogSlice = createSlice({
    name: 'inviteExternalUserDialog',
    initialState: {
        isSaving: false,
        isOpen: false
    },
    reducers: {
        openInviteExternalUserDialog: (state, action: PayloadAction<boolean>) => {
            state.isOpen = action.payload;
        }
    },
    extraReducers: builder =>
        builder
            .addCase(inviteExternalUser.request, state => {
                state.isSaving = true;
            })
            .addCase(inviteExternalUser.success, state => {
                state.isSaving = false;
                state.isOpen = false;
            })
            .addCase(inviteExternalUser.failure, state => {
                state.isSaving = false;
            })
});
export const { openInviteExternalUserDialog } = inviteExternalUserDialogSlice.actions;

export const loginSuccessAction = createAction<AccountInfo>(EventType.LOGIN_SUCCESS);
export const logoutSuccessful = createAction(EventType.LOGOUT_SUCCESS);
const currentUserSlice = createReducer<AccountInfo | null>(null, builder =>
    builder.addCase(loginSuccessAction, (_, action) => action.payload).addCase(logoutSuccessful, () => null)
);

export default combineReducers({
    users: usersSlice.reducer,
    currentUserPermissions: currentUserPermissionsSlice.reducer,
    availableResources: availableResourcesSlice.reducer,
    ui: combineReducers({
        inviteInternalUserDialog: inviteInternalUserDialogSlice.reducer,
        inviteExternalUserDialog: inviteExternalUserDialogSlice.reducer
    }),
    currentUser: currentUserSlice
});
